import { FC, useMemo, useState } from "react";
import { Settings } from "react-slick";
import { IEventFeedResponse } from "@finbackoffice/websocket-client";
import { convertGamesToSports, useDidUpdateEffect } from "@finbackoffice/site-core";
import { sortByScheduledTime } from "@finbackoffice/fe-core";
import SlickSlider from "components/base/slick-slider/SlickSlider";
import SportsLayout from "components/layout/sportsbook/sports/SportsLayout";
import HomeTournamentGameItem from "../../tournament/game/HomeTournamentGameItem";

type IProps = {
    events: IEventFeedResponse[];
    activeSportId: number | null;
    onSportClickHandler: (sportId: number) => void;
    sliderSpeed: number;
};

const SportGamesContent: FC<IProps> = ({
    events,
    activeSportId,
    onSportClickHandler,
    sliderSpeed,
}) => {
    const [ready, setReady] = useState(!!activeSportId);

    const sports = useMemo(() => convertGamesToSports(events), [events]);

    const activeSportEvents = useMemo(
        () => events.filter((event) => event.sport_id === activeSportId).sort(sortByScheduledTime),
        [activeSportId, events],
    );

    const sliderSettings: Settings = useMemo(
        () => ({
            dots: false,
            variableWidth: true,
            infinite: activeSportEvents.length > 1,
            autoplay: activeSportEvents.length > 1,
            autoplaySpeed: sliderSpeed,
            speed: 700,
            slidesToShow: 2,
            slidesToScroll: 1,
            arrows: false,
            responsive: [
                {
                    breakpoint: 1110,
                    settings: {
                        slidesToShow: 1,
                    },
                },
            ],
        }),
        [activeSportEvents.length, sliderSpeed],
    );

    useDidUpdateEffect(() => {
        if (ready) {
            setReady(false);
        }
    }, [activeSportId]);

    useDidUpdateEffect(() => {
        if (!ready && activeSportId) {
            setReady(true);
        }
    }, [ready, activeSportId]);

    return (
        <SportsLayout
            variant="widget"
            sports={sports}
            activeSportId={activeSportId ?? undefined}
            sliderSettings={{
                arrows: false,
            }}
            onSportClick={onSportClickHandler}>
            {ready && (
                <SlickSlider settings={sliderSettings}>
                    {activeSportEvents.map((game, index) => (
                        <HomeTournamentGameItem
                            key={game.id}
                            game={game}
                            layout="top"
                            index={index}
                        />
                    ))}
                </SlickSlider>
            )}
        </SportsLayout>
    );
};

export default SportGamesContent;
